import { useRef, useEffect, useCallback } from 'react'
import { useVolume } from '../contexts/VolumeContext'

type UseSoundOptions = {
  volume?: number
}

export function useSound(soundFile: string, options?: UseSoundOptions) {
  const { volume: globalVolume } = useVolume()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const isLoadedRef = useRef(false)

  useEffect(() => {
    // Criar e pré-carregar o áudio
    const audio = new Audio()
    audio.preload = 'auto'
    
    // Marcar como carregado quando estiver pronto
    const handleCanPlay = () => {
      isLoadedRef.current = true
    }
    
    const handleError = (e: ErrorEvent) => {
      isLoadedRef.current = false
    }
    
    audio.addEventListener('canplaythrough', handleCanPlay)
    audio.addEventListener('error', handleError as any)
    
    // Definir source e forçar carregamento
    audio.src = soundFile
    audio.load()
    
    audioRef.current = audio

    return () => {
      audio.removeEventListener('canplaythrough', handleCanPlay)
      audio.removeEventListener('error', handleError as any)
      audio.pause()
      audio.src = ''
      audioRef.current = null
      isLoadedRef.current = false
    }
  }, [soundFile])

  const play = useCallback(() => {
    if (audioRef.current) {
      try {
        const specificVolume = options?.volume ?? 1
        const finalVolume = (specificVolume * globalVolume) / 100
        
        audioRef.current.volume = Math.max(0, Math.min(1, finalVolume))
        audioRef.current.currentTime = 0
        
        // Tentar tocar, mesmo se ainda não estiver totalmente carregado
        const playPromise = audioRef.current.play()
        
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
              // Silent fail for expected browser restrictions
            }
          })
        }
      } catch (err) {
        // Silent fail
      }
    }
  }, [globalVolume, options?.volume])

  return play
}